'use client'

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { Filter, X, ChevronDown, Star, ShoppingCart, Grid2X2, Rows3, Columns3, Columns4 } from 'lucide-react'
import { API, api, useAuth } from '../context/AuthContext'
import { useBike } from '../context/BikeContext'
import { useCart } from '../context/CartContext'
import { useAuthModal } from '../context/AuthModalContext'
import { BikeSelectionModal } from '../components/BikeSelectionModal'
import { toast } from 'sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { getProductColorHex } from '../utils/productColors'
import { isLaunchLocked } from '../utils/productLaunch'
import { LaunchBadge, LaunchCountdown } from '../components/LaunchCountdown'

/** Simple string hash → 32-bit unsigned int */
const hashSeed = (value) => {
  let hash = 2166136261
  const str = String(value)
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

/**
 * Deterministic shuffle for a visit seed.
 * Same seed → same order; new page load → new seed → rearranged products.
 */
const shuffleWithSeed = (items, seed) => {
  const arr = [...items]
  let state = hashSeed(seed) || 1

  const next = () => {
    // xorshift32
    state ^= state << 13
    state ^= state >>> 17
    state ^= state << 5
    return (state >>> 0) / 4294967296
  }

  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(next() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

const Products = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const productTypeFromUrl = searchParams.get('product_type')
  const isOutran = searchParams.get('is_outran')
  const excludeNewArrivals = searchParams.get('exclude_new_arrivals') === 'true'

  // New seed every page load / visit → products rearrange on reload
  const visitShuffleSeed = useMemo(
    () => `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    []
  )

  const { selectedBike, setBikeCompatibility } = useBike()
  const { isAuthenticated } = useAuth()
  const { refreshCartCount } = useCart()
  const { openAuth } = useAuthModal()

  const getColorHex = getProductColorHex

  const getProductImage = (product) => {
    if (product.colors?.length > 0 && product.color_images) {
      const firstColor = product.colors[0];
      
      // Try exact match first
      if (product.color_images[firstColor]?.[0]) {
        return product.color_images[firstColor][0];
      }
      
      // Try case-insensitive match
      const normalizedSearch = firstColor.trim().toLowerCase();
      const matchingKey = Object.keys(product.color_images).find(
        key => key.trim().toLowerCase() === normalizedSearch
      );
      
      if (matchingKey && product.color_images[matchingKey]?.[0]) {
        return product.color_images[matchingKey][0];
      }
      
      // If no match for first color, try any available color
      const firstAvailableColorImages = Object.values(product.color_images).find(images => images?.length > 0);
      if (firstAvailableColorImages?.[0]) {
        return firstAvailableColorImages[0];
      }
    }
    return product.images?.[0] || product.thumbnail || product.image || '/placeholder.svg'
  }

  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [brands, setBrands] = useState([])
  const [loading, setLoading] = useState(true)
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [viewMode, setViewMode] = useState('four')
  const [itemsPerPage, setItemsPerPage] = useState('20')
  const [currentPage, setCurrentPage] = useState(1)
  const [isBikeModalOpen, setIsBikeModalOpen] = useState(false)
  const [typeAvailability, setTypeAvailability] = useState({
    spare: false,
    accessory: false,
    gear: false,
    combo: true,
    new_arrival: !excludeNewArrivals,
  })
  const spareCategorySlugFromUrl = searchParams.get('spare_category_slug')
  const brandIdFromUrl = searchParams.get('brand_id')
  const brandNameFromUrl = searchParams.get('brand')
  const isNewArrivalFromUrl = searchParams.get('is_new_arrival') === 'true'

  const [productType, setProductType] = useState(
    isNewArrivalFromUrl ? 'new_arrival' : (productTypeFromUrl || 'all')
  )

  const [filters, setFilters] = useState({
    category: spareCategorySlugFromUrl || searchParams.get('category') || '__all__',
    brand: brandIdFromUrl || '__all__',
    minPrice: '',
    maxPrice: '',
    sortBy: 'latest',
  })

  // Sync from URL — only reset category when type changes via URL
  useEffect(() => {
    const nextType = isNewArrivalFromUrl
      ? 'new_arrival'
      : (productTypeFromUrl || 'all')
    setProductType(nextType)
    setFilters((prev) => ({
      ...prev,
      category: spareCategorySlugFromUrl || '__all__',
    }))
  }, [productTypeFromUrl, spareCategorySlugFromUrl, isNewArrivalFromUrl])

  const handleProductTypeChange = (value) => {
    setProductType(value)
    setFilters((prev) => ({
      ...prev,
      category: '__all__',
    }))
    setCurrentPage(1)
  }

  useEffect(() => {
    if (brandIdFromUrl) {
      setFilters((prev) => ({
        ...prev,
        brand: brandIdFromUrl,
      }))
    }
  }, [brandIdFromUrl])

  const fetchBrands = async () => {
    try {
      const brandsRes = await axios.get(`${API}/brands`)
      setBrands(brandsRes.data || [])
    } catch (err) {
      console.error('Failed to fetch brands:', err)
    }
  }

  const fetchTypeAvailability = async () => {
    try {
      const res = await axios.get(`${API}/product-type-availability`)
      setTypeAvailability({
        spare: Boolean(res.data?.spare),
        accessory: Boolean(res.data?.accessory),
        gear: Boolean(res.data?.gear),
        // Always keep combo visible, but hide new arrivals if excluded
        combo: true,
        new_arrival: !excludeNewArrivals,
      })
    } catch (err) {
      console.error('Failed to fetch product type availability:', err)
      // Fallback: show all options except new arrivals if excluded
      setTypeAvailability({
        spare: true,
        accessory: true,
        gear: true,
        combo: true,
        new_arrival: !excludeNewArrivals,
      })
    }
  }

  // Categories: only those that have products for the selected type
  const fetchCategories = useCallback(async () => {
    if (productType === 'all' || productType === 'new_arrival') {
      setCategories([])
      return
    }

    try {
      const params = { only_with_products: true }
      if (productType === 'spare' || productType === 'accessory' || productType === 'gear') {
        params.category_type = productType
      }
      // combo → all typed categories that have products/combos

      const categoriesRes = await axios.get(`${API}/spare-categories`, { params })
      setCategories(categoriesRes.data || [])
    } catch (err) {
      console.error('Failed to fetch categories:', err)
      setCategories([])
    }
  }, [productType])

  useEffect(() => {
    fetchBrands()
    fetchTypeAvailability()
  }, [excludeNewArrivals])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  // If current type has no products, fall back to new_arrival / combo / all
  useEffect(() => {
    if (productType === 'spare' && !typeAvailability.spare) {
      setProductType(typeAvailability.new_arrival ? 'new_arrival' : 'combo')
    } else if (productType === 'accessory' && !typeAvailability.accessory) {
      setProductType(typeAvailability.new_arrival ? 'new_arrival' : 'combo')
    } else if (productType === 'gear' && !typeAvailability.gear) {
      setProductType(typeAvailability.new_arrival ? 'new_arrival' : 'combo')
    } else if (productType === 'new_arrival' && !typeAvailability.new_arrival) {
      setProductType('combo')
    }
  }, [typeAvailability, productType])

  useEffect(() => {
    if (brandNameFromUrl && brands.length > 0) {
      const brand = brands.find((b) => b.name === brandNameFromUrl)
      if (brand) {
        setFilters((prev) => ({
          ...prev,
          brand: brand.id,
        }))
      }
    }
  }, [brandNameFromUrl, brands])

  const fetchProducts = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}

      if (productType === 'new_arrival') {
        params.is_new_arrival = true
      } else if (
        productType === 'spare' ||
        productType === 'accessory' ||
        productType === 'gear' ||
        productType === 'combo'
      ) {
        params.product_type = productType
      }

      const categorySlug =
        spareCategorySlugFromUrl ||
        (filters.category !== '__all__' ? filters.category : null)

      if (categorySlug) {
        params.spare_category_slug = categorySlug
      }

      if (filters.brand !== '__all__') {
        params.brand_id = filters.brand
      }

      if (filters.minPrice) {
        params.min_price = filters.minPrice
      }

      if (filters.maxPrice) {
        params.max_price = filters.maxPrice
      }

      if (selectedBike) {
        params.bike_brand = selectedBike.brand
        params.bike_model = selectedBike.model
        params.bike_variant = selectedBike.variant
      }

      if (isOutran === 'true') {
        params.is_outran = true
      }

      // Fetch all products to enable client-side pagination
      params.limit = 1000

      let data = []

      if (productType === 'combo') {
        const res = await axios.get(`${API}/combos`, { params })
        data = res.data?.combos || []
      } else if (productType === 'all') {
        // When 'all' is selected, fetch both products and combos
        const [productsRes, combosRes] = await Promise.all([
          axios.get(`${API}/products`, { params }),
          axios.get(`${API}/combos`, { params })
        ])
        const products = Array.isArray(productsRes.data) ? productsRes.data : []
        const combos = Array.isArray(combosRes.data?.combos) ? combosRes.data.combos : []
        data = [...products, ...combos]
      } else {
        const res = await axios.get(`${API}/products`, { params })
        data = Array.isArray(res.data) ? res.data : []
      }

      // Client-side safety: never mix other product types into the list
      if (productType === 'spare' || productType === 'accessory' || productType === 'gear') {
        data = data.filter((p) => p.product_type === productType)
      } else if (productType === 'combo') {
        data = data.filter(
          (p) => p.product_type === 'combo' || p.item_type === 'combo'
        )
      } else if (productType === 'new_arrival') {
        data = data.filter((p) => p.is_new_arrival)
      }

      // Exclude new arrivals if flag is set
      if (excludeNewArrivals) {
        data = data.filter((p) => !p.is_new_arrival)
      }

      if (categorySlug) {
        data = data.filter((p) => p.spare_category_slug === categorySlug)
      }

      const productGroupRank = (product) => {
        if (!excludeNewArrivals && product.is_new_arrival) return 0
        if (product.is_best_seller) return 1
        if (product.product_type === 'accessory') return 2
        if (product.product_type === 'gear') return 3
        if (product.product_type === 'spare') return 4
        return 5
      }

      const sortWithinGroup = (a, b) => {
        if (filters.sortBy === 'price_low') return (a.price || 0) - (b.price || 0)
        if (filters.sortBy === 'price_high') return (b.price || 0) - (a.price || 0)
        if (filters.sortBy === 'popularity') return (b.reviews_count || 0) - (a.reviews_count || 0)
        if (filters.sortBy === 'rating') return (b.rating || 0) - (a.rating || 0)
        return new Date(b.created_at || 0) - new Date(a.created_at || 0)
      }

      // Explicit sorts (price / rating / etc.) keep a fixed order.
      // Default "latest" uses a visit-seeded shuffle so reload shows a fresh mix.
      if (filters.sortBy === 'latest') {
        data = shuffleWithSeed(data, visitShuffleSeed)
        // Keep featured groups near the top, but shuffled within each group
        data.sort((a, b) => productGroupRank(a) - productGroupRank(b))
      } else {
        data.sort((a, b) => {
          const groupCompare = productGroupRank(a) - productGroupRank(b)
          return groupCompare || sortWithinGroup(a, b)
        })
      }

      // Remove duplicates based on product ID
      const uniqueProducts = Array.from(
        new Map(data.map(item => [item.id, item])).values()
      )
      
      // Debug logging
      console.log(`Products fetched: ${data.length} products (productType: ${productType})`)
      console.log(`Unique products after deduplication: ${uniqueProducts.length}`)
      console.log('Fetch params:', params)
      
      setProducts(uniqueProducts)
    } catch (err) {
      console.error('Failed to fetch products:', err)
      setProducts([])
    } finally {
      setLoading(false)
    }
  }, [filters, selectedBike, productType, spareCategorySlugFromUrl, isOutran, excludeNewArrivals, visitShuffleSeed])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  // Reset to page 1 when items per page changes
  useEffect(() => {
    setCurrentPage(1)
  }, [itemsPerPage])

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setCurrentPage(1)
  }

  const isSpareFlow = productType === 'spare' || Boolean(spareCategorySlugFromUrl)

  const clearFilters = () => {
    setFilters({
      category: '__all__',
      brand: '__all__',
      minPrice: '',
      maxPrice: '',
      sortBy: 'latest',
    })
    setCurrentPage(1)
  }

  // Calculate pagination
  const totalPages = Math.ceil(products.length / Number(itemsPerPage))
  const startIndex = (currentPage - 1) * Number(itemsPerPage)
  const endIndex = startIndex + Number(itemsPerPage)
  const paginatedProducts = products.slice(startIndex, endIndex)
  
  // Debug logging for pagination
  console.log(`Pagination: Total products=${products.length}, Items per page=${itemsPerPage}, Current page=${currentPage}, Displaying=${paginatedProducts.length} products (indices ${startIndex}-${endIndex})`)

  const productTypeLabel =
    productType === 'all'
      ? 'products'
      : productType === 'new_arrival'
        ? 'new arrivals'
        : productType === 'combo'
          ? 'combos'
          : `${productType}s`

  const showCategoryFilter =
    productType === 'spare' ||
    productType === 'accessory' ||
    productType === 'gear' ||
    productType === 'combo'

  const handleAddToCart = async (product) => {
    if (!isAuthenticated) {
      toast.error('Please login to continue')
      openAuth('login')
      return
    }

    if (isLaunchLocked(product)) {
      toast.error(product.launch_badge || 'This product is launching soon')
      return
    }

    // Check if product requires color/size selection
    const hasColors = product.colors && product.colors.length > 0
    const hasSizes = product.sizes && product.sizes.length > 0
    const hasVariantStock = product.variant_stock && product.variant_stock.length > 0
    
    // If product has variants, navigate to product details instead of adding directly
    if (hasColors || hasSizes || hasVariantStock) {
      navigate(`/products/${product.id}`)
      return
    }

    if (product.product_type === 'spare' && !selectedBike) {
      toast.info('Select your bike for compatible spares')
      setIsBikeModalOpen(true)
      return
    }

    if (product.product_type === 'combo' || product.item_type === 'combo') {
      toast.info('Select variants on product details')
      navigate(`/products/${product.id}`)
      return
    }

    try {
      await api.post('/cart/add', {
        product_id: product.id,
        quantity: 1,
        color: product.colors?.[0] || null,
      })
      const variantInfo = [product.colors?.[0]].filter(Boolean).join(', ');
      toast.success(`${product.name}${variantInfo ? ` (${variantInfo})` : ''} added to cart!`)
      refreshCartCount()
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to add to cart';
      
      // Don't show stock error toast if product has variants - user needs to select different variant
      if (hasColors || hasSizes || hasVariantStock) {
        if (errorMessage.toLowerCase().includes('stock') || errorMessage.toLowerCase().includes('out of stock')) {
          toast.error('Please select a different variant');
          return;
        }
      }
      
      toast.error(errorMessage);
    }
  }

  return (
    <div className="min-h-screen bg-black pb-16">
      {/* Adjust padding based on bike selection */}
      {selectedBike && <div className="h-10" />}
      <div className="h-16" />
      
      <div className="max-w-7xl mx-auto px-4">

        {/* Header */}
        <div className="mb-8">
          {selectedBike && (
            <p className="text-zinc-400 text-sm sm:text-base mb-2">
              Compatible with <span className="text-[#ffcc32] font-semibold">{selectedBike.brand} {selectedBike.model} {selectedBike.variant}</span>
            </p>
          )}
          {isOutran === 'true' && (
            <div className="bg-gradient-to-r from-[#ffcc32]/20 to-[#ffcc32]/10 border border-[#ffcc32]/30 rounded-lg p-4 mb-4">
              <h2 className="text-2xl md:text-3xl font-black uppercase text-white tracking-wider mb-2" style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.1em" }}>
                Outran <span className="text-[#ffcc32]">Originals</span>
              </h2>
              <p className="text-zinc-300 text-sm md:text-base">
                Premium quality products exclusively available at RRide Moto Studio.
              </p>
            </div>
          )}
          {productType === 'combo' && (
            <div className="bg-gradient-to-r from-[#ffcc32]/20 to-[#ff6b35]/20 border border-[#ffcc32]/30 rounded-lg p-4 mb-4">
              <h2 className="text-2xl md:text-3xl font-black uppercase text-white tracking-wider mb-2" style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.1em" }}>
                Combo <span className="text-[#ffcc32]">Offers</span>
              </h2>
              <p className="text-zinc-300 text-sm md:text-base">
                Get more for less! Explore our exclusive combo deals and save big on premium products.
              </p>
            </div>
          )}
        </div>

       

        {/* Spares & Accessories Dropdown */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="w-full sm:w-64">
            <label className="text-zinc-400 text-xs font-semibold uppercase block mb-2">Product Type</label>
            <Select value={productType} onValueChange={handleProductTypeChange}>
              <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="all" className="text-white">
                  All Products
                </SelectItem>

                {/* Show New Arrivals only if not excluded */}
                {typeAvailability.new_arrival && (
                  <SelectItem value="new_arrival" className="text-white">
                    New Arrivals
                  </SelectItem>
                )}

                {typeAvailability.spare && (
                  <SelectItem value="spare" className="text-white">
                    Spares
                  </SelectItem>
                )}

                {typeAvailability.accessory && (
                  <SelectItem value="accessory" className="text-white">
                    Accessories
                  </SelectItem>
                )}

                {typeAvailability.gear && (
                  <SelectItem value="gear" className="text-white">
                    Gears
                  </SelectItem>
                )}

                <SelectItem value="combo" className="text-white">
                  Combos
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-zinc-400 text-lg">
            Showing <span className="text-white font-semibold">{startIndex + 1}-{Math.min(endIndex, products.length)}</span>{" "}
            of <span className="text-white font-semibold">{products.length}</span>{" "}
            {productTypeLabel}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Filters Sidebar - Desktop */}
          <div className="hidden lg:block lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 sticky top-24 h-fit"
            >
              <h3 className="text-white font-bold text-lg uppercase mb-6" style={{ fontFamily: 'Cinzel, serif', letterSpacing: "0.1em" }}>
                Filters
              </h3>

              {/* Category Filter - only categories that have products */}
              {showCategoryFilter && (
                <div className="mb-6">
                  <label className="text-zinc-400 text-xs font-semibold mb-3 block uppercase">Category</label>
                  <Select value={filters.category} onValueChange={(val) => updateFilter('category', val)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-full">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      <SelectItem value="__all__">All Categories</SelectItem>
                      {categories.map((c) => (
                        <SelectItem key={c.id || c.slug} value={c.slug} className="text-white">
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {categories.length === 0 && (
                    <p className="text-xs text-zinc-500 mt-2">No categories with products for this type.</p>
                  )}
                </div>
              )}

              {/* Brand Filter */}
              <div className="mb-6">
                <label className="text-zinc-400 text-xs font-semibold mb-3 block uppercase">Brand</label>
                <Select value={filters.brand} onValueChange={(val) => updateFilter('brand', val)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-full">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="__all__">All Brands</SelectItem>
                    {brands.map((b) => (
                      <SelectItem key={b.id} value={b.id} className="text-white">
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Price Range Filter */}
              <div className="mb-6">
                <label className="text-zinc-400 text-xs font-semibold mb-3 block uppercase">Price Range</label>
                <div className="space-y-2">
                  <input
                    type="number"
                    placeholder="Min Price"
                    value={filters.minPrice}
                    onChange={(e) => updateFilter('minPrice', e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm rounded placeholder-zinc-600 focus:outline-none focus:border-orange-500"
                  />
                  <input
                    type="number"
                    placeholder="Max Price"
                    value={filters.maxPrice}
                    onChange={(e) => updateFilter('maxPrice', e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm rounded placeholder-zinc-600 focus:outline-none focus:border-orange-500"
                  />
                </div>
              </div>

              {/* Sort Filter */}
              <div className="mb-6">
                <label className="text-zinc-400 text-xs font-semibold mb-3 block uppercase">Sort By</label>
                <Select value={filters.sortBy} onValueChange={(val) => updateFilter('sortBy', val)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-full">
                    <SelectValue placeholder="Latest" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="latest">Latest</SelectItem>
                    <SelectItem value="price_low">Price: Low to High</SelectItem>
                    <SelectItem value="price_high">Price: High to Low</SelectItem>
                    <SelectItem value="popularity">Popularity</SelectItem>
                    <SelectItem value="rating">Highest Rated</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters Button */}
              {(filters.category !== '__all__' ||
                filters.brand !== '__all__' ||
                filters.minPrice ||
                filters.maxPrice ||
                filters.sortBy !== 'latest') && (
                <button
                  onClick={clearFilters}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-zinc-800 hover:bg-zinc-700 text-white rounded transition-colors text-sm font-semibold"
                >
                  <X size={16} /> Clear All Filters
                </button>
              )}
            </motion.div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-4">
            {/* Mobile Filters Toggle */}
            <div className="lg:hidden mb-6">
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="flex items-center gap-2 px-4 py-3 bg-zinc-900 border border-zinc-800 text-white rounded-lg w-full font-semibold"
              >
                <Filter size={18} />
                Filters
                <ChevronDown size={18} className={`ml-auto transition-transform ${showMobileFilters ? 'rotate-180' : ''}`} />
              </button>

              {/* Mobile Filters Expanded */}
              {showMobileFilters && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-4"
                >
                  {showCategoryFilter && (
                    <div>
                      <label className="text-zinc-400 text-xs font-semibold mb-2 block uppercase">Category</label>
                      <Select value={filters.category} onValueChange={(val) => updateFilter('category', val)}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700">
                          <SelectItem value="__all__">All Categories</SelectItem>
                          {categories.map((c) => (
                            <SelectItem key={c.id || c.slug} value={c.slug} className="text-white">
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <label className="text-zinc-400 text-xs font-semibold mb-2 block uppercase">Brand</label>
                    <Select value={filters.brand} onValueChange={(val) => updateFilter('brand', val)}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        <SelectItem value="__all__">All Brands</SelectItem>
                        {brands.map((b) => (
                          <SelectItem key={b.id} value={b.id} className="text-white">
                            {b.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-zinc-400 text-xs font-semibold mb-2 block uppercase">Sort</label>
                    <Select value={filters.sortBy} onValueChange={(val) => updateFilter('sortBy', val)}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        <SelectItem value="latest">Latest</SelectItem>
                        <SelectItem value="price_low">Price: Low to High</SelectItem>
                        <SelectItem value="price_high">Price: High to Low</SelectItem>
                        <SelectItem value="popularity">Popularity</SelectItem>
                        <SelectItem value="rating">Highest Rated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </motion.div>
              )}
            </div>

            <div className="hidden md:flex items-center justify-between gap-4 mb-8 border-y border-white/10 py-5">
              <div className="flex items-center gap-3">
                <span className="text-white uppercase font-black text-sm">View As</span>
                {[
                  ['rows', Rows3],
                  ['two', Grid2X2],
                  ['three', Columns3],
                  ['four', Columns4],
                ].map(([mode, Icon]) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`w-10 h-10 border flex items-center justify-center transition ${viewMode === mode ? 'bg-[#ffcc32] border-[#ffcc32] text-black' : 'border-white/20 text-zinc-400 hover:text-white'}`}
                    title={mode}
                  >
                    <Icon size={20} />
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-3">
                  <span className="text-white uppercase font-black text-sm">Items Per Page</span>
                  <Select value={itemsPerPage} onValueChange={setItemsPerPage}>
                    <SelectTrigger className="w-28 bg-white text-black border-white rounded-none font-semibold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="40">40</SelectItem>
                      <SelectItem value="60">60</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label className="flex items-center gap-3">
                  <span className="text-white uppercase font-black text-sm">Sort By</span>
                  <Select value={filters.sortBy} onValueChange={(val) => updateFilter('sortBy', val)}>
                    <SelectTrigger className="w-44 bg-white text-black border-white rounded-none font-semibold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="latest">Most relevant</SelectItem>
                      <SelectItem value="price_low">Price: Low to High</SelectItem>
                      <SelectItem value="price_high">Price: High to Low</SelectItem>
                      <SelectItem value="popularity">Popularity</SelectItem>
                      <SelectItem value="rating">Highest Rated</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              </div>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="text-center py-20">
                <p className="text-zinc-400 text-lg">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-zinc-400 text-lg">No products found. Try adjusting your filters.</p>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`grid gap-5 ${
                  viewMode === 'rows'
                    ? 'grid-cols-1'
                    : viewMode === 'two'
                      ? 'grid-cols-2'
                      : viewMode === 'three'
                        ? 'grid-cols-2 lg:grid-cols-3'
                        : 'grid-cols-2 lg:grid-cols-4'
                }`}
              >
                {paginatedProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => navigate(`/products/${product.id}`)}
                    className={`bg-[#0b0b0b] border border-white/10 cursor-pointer rounded-lg overflow-hidden group flex flex-col h-full ${viewMode === 'rows' ? 'grid md:grid-cols-[260px_1fr]' : ''}`}
                  >
                    {/* Image */}
                    <div
                      className={`relative overflow-hidden bg-zinc-900 flex-shrink-0 ${viewMode === 'rows' ? 'h-64 md:h-full' : 'aspect-[4/3]'}`}
                    >
                      <img
                        src={getProductImage(product)}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />

                      {/* Color Display - First Color (Most Selling) */}
                      {product.colors && product.colors.length > 0 && (
                        <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
                          <span
                            className="w-4 h-4 rounded-full border-2 border-white/30"
                            style={{ backgroundColor: getColorHex(product.colors[0]) }}
                          />
                          <span className="text-white text-xs font-semibold capitalize">
                            {product.colors[0]}
                          </span>
                          <span className="text-[#ffcc32] text-xs font-bold ml-1">Most Selling</span>
                          {product.colors.length > 1 && (
                            <span className="text-zinc-400 text-xs">+{product.colors.length - 1}</span>
                          )}
                        </div>
                      )}

                      {/* Badges */}
                      <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
                        <LaunchBadge product={product} />
                      </div>
                      <div className="absolute top-3 right-3 flex gap-2 flex-wrap">
                        {(product.product_type === 'combo' || product.item_type === 'combo') && (
                          <div className="bg-[#ff6b35] text-white px-2 py-1 text-xs font-bold uppercase">
                            Combo Deal
                          </div>
                        )}
                        {product.is_outran && (
                          <div className="bg-[#ffcc32] text-black px-2 py-1 text-xs font-bold uppercase">
                            Outran Product
                          </div>
                        )}
                        {product.is_desktop && (
                          <div className="bg-[#3b82f6] text-white px-2 py-1 text-xs font-bold uppercase">
                            Desktop Product
                          </div>
                        )}
                        {product.is_best_seller && (
                          <div className="bg-[#ff2b20] text-white px-2 py-1 text-xs font-bold uppercase">
                            Best Seller
                          </div>
                        )}
                        {product.is_new_arrival && (
                          <div className="bg-[#ffcc32] text-black px-2 py-1 text-xs font-bold uppercase">
                            New
                          </div>
                        )}
                      </div>

                      <div className="absolute bottom-3 left-3 right-14 z-10">
                        <LaunchCountdown product={product} compact />
                      </div>

                      {/* Add to Cart */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleAddToCart(product)
                        }}
                        disabled={isLaunchLocked(product)}
                        className={`absolute bottom-3 right-3 w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10 ${
                          isLaunchLocked(product)
                            ? 'bg-zinc-700 text-zinc-300 cursor-not-allowed'
                            : 'bg-[#ffcc32] hover:bg-[#ffda5c] text-black'
                        }`}
                        title={isLaunchLocked(product) ? (product.launch_badge || 'Coming Soon') : 'Add to cart'}
                      >
                        <ShoppingCart size={19} />
                      </button>
                    </div>

                    {/* Details */}
                    <div className="p-3 flex flex-col flex-grow">
                      {/* Product Name - Fixed height with line clamp */}
                      <h3 className="text-white font-black text-base md:text-lg uppercase line-clamp-2 mb-2 leading-relaxed min-h-[2.5rem] max-h-[2.5rem]" style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.25rem', letterSpacing: '0.5px' }}>
                        {product.name}
                      </h3>

                      {/* Rating */}
                      <div className="flex items-center gap-1 mb-2">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={12}
                              className={i < Math.round(product.rating) ? 'fill-yellow-500 text-yellow-500' : 'text-zinc-600'}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Price - Improved typography */}
                      <div className="mb-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-black text-lg md:text-xl leading-relaxed tracking-wide" style={{ fontFamily: 'Arial, sans-serif' }}>
                            Rs. {product.price?.toLocaleString()}
                          </span>
                          {product.original_price && (
                            <span className="text-zinc-400 line-through text-sm leading-relaxed" style={{ fontFamily: 'Arial, sans-serif' }}>
                              Rs. {product.original_price?.toLocaleString()}
                            </span>
                          )}
                        </div>
                        {product.original_price && (
                          <div className="text-[#ffcc32] text-xs font-bold leading-relaxed" style={{ fontFamily: 'Arial, sans-serif' }}>
                            Save {Math.round(((product.original_price - product.price) / product.original_price) * 100)}%
                          </div>
                        )}
                      </div>

                      {/* Spacer to push button to bottom */}
                      <div className="flex-grow"></div>

                      {/* Shop Now Button - Always at bottom */}
                      <button
                        className="mt-2 w-full rounded-full bg-[#ffcc32] py-2.5 text-black font-black uppercase text-sm md:text-base"
                        style={{ fontFamily: 'Arial, sans-serif', fontWeight: 800, letterSpacing: '1px' }}
                      >
                        SHOP NOW
                      </button>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-8 mb-4">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-zinc-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-700 transition"
                >
                  Previous
                </button>
                <div className="flex gap-2">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-4 py-2 rounded-lg transition ${
                        currentPage === i + 1
                          ? 'bg-[#ffcc32] text-black font-bold'
                          : 'bg-zinc-800 text-white hover:bg-zinc-700'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-zinc-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-700 transition"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <BikeSelectionModal
        isOpen={isBikeModalOpen}
        onClose={() => setIsBikeModalOpen(false)}
        onBikeSelect={(brand, model, variant) => {
          setBikeCompatibility(brand, model, variant)
          setIsBikeModalOpen(false)
        }}
      />
    </div>
  )
}

export default Products
